/*
  Video Shopping — tema-native alışveriş videoları (Be Yours 9.5.1).

  Kart önizlemesi: <video> bir <template> içinde durur, kart görünür olunca
  (IntersectionObserver) eklenip sessiz/döngülü oynar; ekrandan çıkınca durur.
  Oynatıcı: scroll-snap tabanlı (masaüstü yatay/ortalı, mobil dikey tam ekran),
  aktif slayt IntersectionObserver ile belirlenir, yalnız aktif + komşu klipler
  yüklenir. Tema yardımcıları (trapFocus, removeTrapFocus, setScrollbarWidth) ve
  tema <add-to-cart> elementi kullanılır; başka bağımlılık yoktur.
*/
(() => {
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const savesData = () => !!(navigator.connection && navigator.connection.saveData);
  const visibleRatio = (el) => {
    const r = el.getBoundingClientRect();
    if (r.height === 0) return 0;
    const seen = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0);
    return seen / r.height;
  };

  /* ---------------------------------------------------------------- kart */
  class VideoShoppingPreview extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.template = this.querySelector('template');
      if (!this.template) return;
      this.mode = this.dataset.autoplay || 'in_view';
      this.host = this.closest('.video-shopping__media') || this;
      if (this.mode === 'off' || savesData()) return;

      if (this.mode === 'in_view' && !prefersReducedMotion()) {
        this.observer = new IntersectionObserver(
          (entries) => entries.forEach((e) => (e.isIntersecting ? this.play() : this.pause())),
          { threshold: 0.5 }
        );
        this.observer.observe(this);
        // Owner 2026-10-16: kaydırırken kart görünür olduğunda <video> sıfırdan kurulup yükleniyor,
        // geçiş takılıyordu. Ray içinde bir ekran ötedeki klip önceden kurulup tamponlanır.
        const rail = this.closest('.video-shopping__grid');
        if (rail && !savesData()) {
          // TPS perf (2026-10-16): ray kökü sayfa kaydırmasını görmez → section ekranın çok altındayken bile
          // komşu klipler açılışta iniyordu (mobil ~1,5 MB, masaüstü ~2,9 MB). Önce ray ekrana bir ekran boyu yaklaşsın.
          this.gate = new IntersectionObserver(
            (entries) => {
              if (!entries.some((e) => e.isIntersecting)) return;
              this.gate.disconnect();
              this.gate = null;
              this.watchNear(rail);
            },
            { rootMargin: '100% 0px' }
          );
          this.gate.observe(rail);
        }
        return;
      }
      // hover modu — ya da hareket azaltma tercihi varken "in_view": klip yalnız
      // ziyaretçi isteyince oynar.
      this.host.addEventListener('pointerenter', () => this.play());
      this.host.addEventListener('pointerleave', () => this.pause());
      this.host.addEventListener('focus', () => this.play());
      this.host.addEventListener('blur', () => this.pause());
    }

    watchNear(rail) {
      if (!this.isConnected) return;
      this.nearObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          // Kart görünür olup play() klibi zaten kurduysa dokunma: video.load() oynayan klibi sıfırlayıp durdurur.
          const fresh = !this.video;
          this.load();
          if (fresh && this.video && this.video.preload !== 'auto') {
            this.video.preload = 'auto';
            this.video.load();
          }
          this.nearObserver.disconnect();
          this.nearObserver = null;
        },
        { root: rail, rootMargin: '0px 100%' }
      );
      this.nearObserver.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      this.observer = null;
      if (this.gate) this.gate.disconnect();
      this.gate = null;
      if (this.nearObserver) this.nearObserver.disconnect();
      this.nearObserver = null;
    }

    load() {
      if (this.video || !this.template) return;
      this.appendChild(this.template.content.firstElementChild.cloneNode(true));
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.video.muted = true;
      this.video.loop = true;
      this.video.playsInline = true;
      this.video.addEventListener('playing', () => this.classList.add('is-playing'));
      this.video.addEventListener('pause', () => this.classList.remove('is-playing'));
    }

    play() {
      if (this.suspended) return;
      this.load();
      if (!this.video) return;
      const played = this.video.play();
      if (played && typeof played.catch === 'function') played.catch(() => {});
    }

    pause() {
      if (this.video) this.video.pause();
    }

    // Oynatıcı açıkken kart klipleri susar; kapanınca görünür olanlar kaldığı yerden devam eder.
    suspend() {
      this.suspended = true;
      this.pause();
    }

    resume() {
      this.suspended = false;
      if (this.mode === 'in_view' && !prefersReducedMotion() && visibleRatio(this) >= 0.5) this.play();
    }
  }
  if (!customElements.get('video-shopping-preview')) customElements.define('video-shopping-preview', VideoShoppingPreview);

  /* ------------------------------------------------------------- klip */
  class VideoShoppingClip extends HTMLElement {
    load() {
      if (this.video) return this.video;
      const template = this.querySelector('template');
      if (!template) return null;
      this.appendChild(template.content.firstElementChild.cloneNode(true));
      this.video = this.querySelector('video');
      if (this.video) this.video.playsInline = true;
      return this.video;
    }
  }
  if (!customElements.get('video-shopping-clip')) customElements.define('video-shopping-clip', VideoShoppingClip);

  /* --------------------------------------------------------- oynatıcı */
  class VideoShoppingPlayer extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;

      this.sectionId = this.dataset.sectionId;
      this.dialog = this.querySelector('[role="dialog"]');
      this.track = this.querySelector('.video-shopping-player__track');
      this.toast = this.querySelector('.video-shopping-player__toast');
      this.prevButton = this.querySelector('[data-nav="-1"]');
      this.nextButton = this.querySelector('[data-nav="1"]');
      // TPS perf (2026-10-16): slaytlar <template data-slides> içinde gelir (açılmadan DOM'da ~480 öğe tutmasın);
      // ilk open() çağrısında ensureSlides() ile kurulur. O zamana kadar this.slides boş, sayı şablondan okunur.
      this.slidesTemplate = this.track ? this.track.querySelector(':scope > template[data-slides]') : null;
      this.slides = this.track ? Array.from(this.track.children).filter((el) => el.tagName !== 'TEMPLATE') : [];
      const slideCount = this.slidesTemplate ? this.slidesTemplate.content.children.length : this.slides.length;
      this.baseLabel = this.dialog ? this.dialog.getAttribute('aria-label') : '';
      this.labels = { play: this.dataset.labelPlay || 'Play', pause: this.dataset.labelPause || 'Pause' };
      this.muted = this.dataset.startMuted === 'true';
      this.index = -1;
      if (!this.dialog || !this.track || slideCount === 0) return;

      this.section = document.getElementById(`VideoShopping-${this.sectionId}`);
      if (this.section) {
        this.section.addEventListener('click', (event) => {
          const opener = event.target.closest('[data-open-player]');
          if (!opener || opener.disabled) return;
          event.preventDefault();
          this.open(Number(opener.dataset.openPlayer), opener);
        });
      }

      this.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.querySelectorAll('[data-nav]').forEach((el) =>
        el.addEventListener('click', () => this.go(this.index + Number(el.dataset.nav)))
      );
      const share = this.querySelector('[data-share]');
      if (share) share.addEventListener('click', () => this.share());

      this.track.addEventListener('click', this.onTrackClick.bind(this));
      this.track.addEventListener('pointerdown', (event) => {
        const bar = event.target.closest('[data-progress]');
        if (bar) this.seek(bar.closest('.video-shopping-player__slide'), event);
      });
      this.addEventListener('keydown', this.onKeydown.bind(this));

      // <add-to-cart> bir <button> değil: role=button taşıyor, klavye için Enter/boşluk tıklamaya çevrilir.
      [this.section, this].forEach((root) => {
        if (!root) return;
        root.addEventListener('keydown', (event) => {
          if ((event.key === 'Enter' || event.key === ' ') && event.target.matches('add-to-cart')) {
            event.preventDefault();
            event.target.click();
          }
        });
      });

      // Aktif slayt = merkezi ray merkezine en yakın slayt. Kaydırma, ok, klavye — hepsi
      // buradan geçer. Kesişim oranı kullanılmaz: ölçeklenmiş komşu slaytlar da tamamen
      // görünür olduğundan oran hepsinde 1'e çıkar ve yanlış slaytı seçer.
      this.track.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = window.requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.syncActive();
        });
      });
      this.track.addEventListener('scrollend', () => this.syncActive());

      // Sepete ekleme temanın çekmecesini açar; oynatıcı üstte kalmasın.
      document.addEventListener('ajaxProduct:added', () => {
        if (this.isOpen) this.close();
      });

      // Paylaşılan bağlantı: ?video-shopping=<sectionId>:<index> → sessiz açılır (jest olmadan ses yok).
      const deepLink = new URLSearchParams(window.location.search).get('video-shopping');
      if (deepLink) {
        const [sectionId, index] = deepLink.split(':');
        if (sectionId === this.sectionId) {
          this.forceMuted = true; // jest yok → sesli oynatma izni yok
          window.requestAnimationFrame(() => this.open(Number(index) || 0, null));
        }
      }

      // Tema editörü: blok seçilince kartı görünür yap.
      document.addEventListener('shopify:block:select', (event) => {
        if (!this.section || !this.section.contains(event.target)) return;
        event.target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      });
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target.contains(this)) this.close();
      });
    }

    get isOpen() {
      return !this.hidden;
    }

    get isVertical() {
      return window.getComputedStyle(this.track).scrollSnapType.indexOf('y') === 0;
    }

    ensureSlides() {
      if (!this.slidesTemplate) return;
      this.track.appendChild(this.slidesTemplate.content);
      this.slidesTemplate.remove();
      this.slidesTemplate = null;
      this.slides = Array.from(this.track.children);
    }

    open(index, opener) {
      this.ensureSlides();
      if (!this.slides[index]) index = 0;
      this.opener = opener || document.activeElement;
      document.querySelectorAll('video-shopping-preview').forEach((preview) => preview.suspend());
      if (typeof window.setScrollbarWidth === 'function') window.setScrollbarWidth();
      document.body.classList.add('overflow-hidden');
      this.hidden = false;
      this.muted = this.forceMuted || this.dataset.startMuted === 'true';
      this.forceMuted = false;
      this.scrollToSlide(index, 'instant');
      this.activate(index, true);
      if (typeof window.trapFocus === 'function') window.trapFocus(this, this.dialog);
      else this.dialog.focus();
    }

    close() {
      if (!this.isOpen) return;
      this.slides.forEach((slide) => this.pauseSlide(slide, false));
      this.hidden = true;
      document.body.classList.remove('overflow-hidden');
      if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(this.opener);
      else if (this.opener && typeof this.opener.focus === 'function') this.opener.focus();
      document.querySelectorAll('video-shopping-preview').forEach((preview) => preview.resume());
      this.index = -1;
      this.slides.forEach((slide) => {
        slide.classList.remove('is-active');
        slide.removeAttribute('inert');
      });
      if (this.scrollFrame) {
        window.cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
      }
    }

    scrollToSlide(index, behavior) {
      const slide = this.slides[index];
      if (!slide) return;
      if (this.isVertical) {
        this.track.scrollTo({ top: slide.offsetTop, behavior });
      } else {
        this.track.scrollTo({ left: slide.offsetLeft - (this.track.clientWidth - slide.clientWidth) / 2, behavior });
      }
    }

    go(index) {
      if (index < 0 || index >= this.slides.length) return;
      this.scrollToSlide(index, prefersReducedMotion() ? 'instant' : 'smooth');
    }

    // Dönüşümden etkilenmeyen offset ölçüleriyle ray merkezine en yakın slayt.
    syncActive() {
      if (!this.isOpen) return;
      const vertical = this.isVertical;
      const center = vertical
        ? this.track.scrollTop + this.track.clientHeight / 2
        : this.track.scrollLeft + this.track.clientWidth / 2;
      let best = this.index;
      let bestDistance = Infinity;
      this.slides.forEach((slide, i) => {
        const slideCenter = vertical
          ? slide.offsetTop + slide.offsetHeight / 2
          : slide.offsetLeft + slide.offsetWidth / 2;
        const distance = Math.abs(slideCenter - center);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });
      if (best !== this.index) this.activate(best);
    }

    activate(index, force) {
      if (!this.isOpen) return;
      if (index === this.index && !force) return;
      const previous = this.slides[this.index];
      if (previous && previous !== this.slides[index]) this.pauseSlide(previous, true);

      this.index = index;
      const slide = this.slides[index];
      // Yalnız aktif slaytın kontrolleri klavyeye açık; komşular inert (odak ve ekran okuyucu dışı).
      this.slides.forEach((item, i) => {
        item.classList.toggle('is-active', i === index);
        item.toggleAttribute('inert', i !== index);
      });
      this.playSlide(slide);

      // Komşular: meta veriyi çek ki geçiş beklemesin; tamamını indirme.
      [index - 1, index + 1].forEach((i) => {
        const clip = this.slides[i] && this.slides[i].querySelector('video-shopping-clip');
        if (!clip) return;
        const video = clip.load();
        if (video && video.preload === 'none') video.preload = 'metadata';
      });

      if (this.prevButton) this.prevButton.disabled = index === 0;
      if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;
      this.dialog.setAttribute('aria-label', slide.dataset.title ? `${this.baseLabel}: ${slide.dataset.title}` : this.baseLabel);
    }

    playSlide(slide) {
      const clip = slide.querySelector('video-shopping-clip');
      if (!clip) return;
      const video = clip.load();
      if (!video) return;
      this.bindVideo(slide, video);
      video.muted = this.muted;
      this.syncSound(slide);
      const played = video.play();
      if (played && typeof played.catch === 'function') {
        // Yalnız izin reddinde (NotAllowedError) sessize alıp tekrar dene. Hızlı slayt
        // değişiminde play() → pause() kesintisi AbortError verir; o ses tercihine dokunmaz.
        played.catch((error) => {
          if (video.muted || !error || error.name !== 'NotAllowedError') return;
          this.setMuted(true);
          const retry = video.play();
          if (retry && typeof retry.catch === 'function') retry.catch(() => {});
        });
      }
    }

    pauseSlide(slide, reset) {
      const video = slide.querySelector('video');
      if (!video) return;
      video.pause();
      if (reset) video.currentTime = 0;
    }

    bindVideo(slide, video) {
      if (video.dataset.bound) return;
      video.dataset.bound = 'true';
      const bar = slide.querySelector('progress');
      const toggle = slide.querySelector('[data-toggle]');
      video.addEventListener('timeupdate', () => {
        if (bar && video.duration) bar.value = (video.currentTime / video.duration) * 100;
      });
      video.addEventListener('play', () => {
        slide.classList.add('is-playing');
        if (toggle) {
          toggle.setAttribute('aria-pressed', 'false');
          toggle.setAttribute('aria-label', this.labels.pause);
        }
      });
      video.addEventListener('pause', () => {
        slide.classList.remove('is-playing');
        if (toggle) {
          toggle.setAttribute('aria-pressed', 'true');
          toggle.setAttribute('aria-label', this.labels.play);
        }
      });
      video.addEventListener('waiting', () => slide.classList.add('is-waiting'));
      video.addEventListener('playing', () => slide.classList.remove('is-waiting'));
    }

    toggle(slide) {
      const video = slide && slide.querySelector('video');
      if (!video) return;
      if (video.paused) {
        const played = video.play();
        if (played && typeof played.catch === 'function') played.catch(() => {});
      } else {
        video.pause();
      }
    }

    setMuted(muted) {
      this.muted = muted;
      this.slides.forEach((slide) => {
        const video = slide.querySelector('video');
        if (video) video.muted = muted;
        this.syncSound(slide);
      });
    }

    syncSound(slide) {
      const button = slide.querySelector('[data-sound]');
      if (button) button.setAttribute('aria-pressed', String(!this.muted));
    }

    seek(slide, event) {
      const video = slide && slide.querySelector('video');
      const bar = slide && slide.querySelector('[data-progress]');
      if (!video || !bar || !video.duration) return;
      const rect = bar.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
      video.currentTime = ratio * video.duration;
    }

    onTrackClick(event) {
      const slide = event.target.closest('.video-shopping-player__slide');
      if (!slide) return;
      if (event.target.closest('[data-sound]')) {
        this.setMuted(!this.muted);
        return;
      }
      if (event.target.closest('[data-progress]')) return;
      if (event.target.closest('[data-toggle], video')) {
        const index = Number(slide.dataset.index);
        if (index !== this.index) {
          this.go(index); // yan önizlemeye tıklama = o videoya geç
          return;
        }
        this.toggle(slide);
      }
    }

    onKeydown(event) {
      const onControl = event.target.matches('a, button, add-to-cart, input');
      switch (event.key) {
        case 'Escape':
          this.close();
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          this.go(this.index + 1);
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          this.go(this.index - 1);
          break;
        case ' ':
        case 'k':
          if (onControl) return;
          event.preventDefault();
          this.toggle(this.slides[this.index]);
          break;
        case 'm':
          if (onControl) return;
          this.setMuted(!this.muted);
          break;
        default:
      }
    }

    share() {
      const url = new URL(window.location.href);
      url.searchParams.set('video-shopping', `${this.sectionId}:${this.index}`);
      const slide = this.slides[this.index];
      const data = { url: url.toString(), title: (slide && slide.dataset.title) || document.title };
      if (navigator.share) {
        navigator.share(data).catch(() => {});
        return;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(data.url).then(() => this.showToast(this.dataset.shareCopied));
      }
    }

    showToast(text) {
      if (!this.toast || !text) return;
      this.toast.textContent = text;
      this.toast.hidden = false;
      window.clearTimeout(this.toastTimer);
      this.toastTimer = window.setTimeout(() => {
        this.toast.hidden = true;
      }, 2200);
    }
  }
  if (!customElements.get('video-shopping-player')) customElements.define('video-shopping-player', VideoShoppingPlayer);
})();
